import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useHistory } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import { Helmet } from "react-helmet";
import { WidgetFooter, WidgetHeader } from "../../widgets";
import ModalEditProfile from "../../modules/ui/Modals/ModalEditProfile";
import ModalSetting from "../../modules/ui/Modals/ModalSetting";
import ModalAuth from "../../modules/ui/Modals/ModalAuth";
import ModalConfirmation from "../../modules/ui/Modals/ModalСonfirmation";
import ModalGroupSetting from "../../modules/ui/Modals/ModalGroupSettings";
import {
  CLOSE_MODAL_AUTH,
  MODAL_TOGGLE,
  NEW_USER_MODAL,
} from "../../store/Modal/Modal.constant";
import { COMMON_CHANGE_SETTINGS } from "../../store/Common/Common.constant";
import AnimatedLoader from "../../modules/ui/animatedLoader";
import { showEmailNotificationSetting, updateNotificationSettings } from "../../shared/services/Api/CommonService";
import "./style.scss";

const EmailNotification = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { slug_uuid } = useParams();
  const token = localStorage.getItem("token");
  const common = useSelector((state) => state.common);
  const modals = useSelector((store) => store.modal[slug_uuid]);
  const form = useSelector((state) => state.form);
  const groupOrders = common.groupOrders?.[slug_uuid];
  const isJoin = true;
  const settings = common.settings?.[slug_uuid];
  const [isLoading, setIsLoading] = useState(false);
  const [formState, setFormState] = useState({
    order_submission_reminder: false,
    delivery_control_additional_email_enabled: false,
    delivery_control_additional_sms: false,
  });
  const [emailInput, setEmailInput] = useState("");
  const [emailList, setEmailList] = useState([]);
  const [emailError, setEmailError] = useState("");
  const [phoneInput, setPhoneInput] = useState("");
  const [phoneList, setPhoneList] = useState([]);

  const validateEmail = (email) => {
    if (!email.includes("@")) return "Missing '@' symbol in email address.";
    const [localPart, domain] = email.split("@");
    if (!localPart) return "Missing username before '@'.";
    if (!domain) return "Missing domain after '@'.";
    if (!domain.includes(".")) return "Domain must contain a dot (e.g., gmail.com).";
    const domainParts = domain.split(".");
    if (domainParts.some((part) => part.trim() === "")) return "Invalid domain structure.";
    const regex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!regex.test(email)) return "Invalid email format.";
    return "";
  };

  const parseEmails = (value) => value.split(",").map((e) => e.trim()).filter(Boolean);

  const validateEmailsInput = (value) => {
    const emails = parseEmails(value);
    for (let email of emails) {
      const error = validateEmail(email);
      if (error) return `${error} ("${email}")`;
    }
    return "";
  };

  const fetchNotificationSettings = async () => {
    try {
      setIsLoading(true);
      const response = await showEmailNotificationSetting(slug_uuid);

      dispatch({
        type: COMMON_CHANGE_SETTINGS,
        payload: {
          user_id: slug_uuid,
          data: {
            notification_settings: response.notification_settings ?? {},
          },
        },
      });

      const existingEmails = response.notification_settings?.delivery_control_email_setting?.delivery_control_settings_emails || "";
      setEmailList(parseEmails(existingEmails));

      const existingPhones = response.notification_settings?.delivery_control_email_setting?.delivery_control_settings_phones || "";
      setPhoneList(existingPhones.split(",").map((p) => p.trim()).filter(Boolean));

      setFormState({
        order_submission_reminder: response.notification_settings?.order_submission_reminder,
        delivery_control_additional_email_enabled: response.notification_settings?.delivery_control_email_setting?.delivery_control_additional_email,
        delivery_control_additional_sms: response.notification_settings?.delivery_control_email_setting?.delivery_control_additional_sms,
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (token) {
      fetchNotificationSettings();
    }
  }, [slug_uuid, token]);

  useEffect(() => {
    if (settings?.auth?.is_dashboard_admin !== true) {
      history.push(`/grouporder-dashboard/${slug_uuid}`);
    }
  });

  useEffect(() => {
    if (settings?.auth?.auth_token !== token) {
      dispatch({ type: NEW_USER_MODAL, payload: slug_uuid });
    }
  }, []);

  const handleChange = (key, value) => {
    setFormState((prev) => ({ ...prev, [key]: value }));
  };

  const handleEmailInputChange = (e) => {
    setEmailInput(e.target.value);
    setEmailError(validateEmailsInput(e.target.value));
  };

  const addEmailsToList = () => {
    const error = validateEmailsInput(emailInput);
    if (error) {
      setEmailError(error);
      return;
    }
    const newEmails = parseEmails(emailInput);
    setEmailList([...emailList, ...newEmails]);
    setEmailInput("");
    setEmailError("");
  };

  const removeEmail = (emailToRemove) => {
    setEmailList(emailList.filter((email) => email !== emailToRemove));
  };

  const handlePhoneInputChange = (e) => {
    setPhoneInput(e.target.value);
  };

  const addPhoneToList = () => {
    if (!phoneInput.trim()) return;
    const newPhones = phoneInput
      .split(",")
      .map((p) => p.trim())
      .filter(Boolean);

    setPhoneList([...phoneList, ...newPhones]);
    setPhoneInput("");
  };
  const removePhone = (phoneToRemove) => {
    setPhoneList(phoneList.filter((p) => p !== phoneToRemove));
  };


  const handleSave = async () => {
    if (emailInput) addEmailsToList();
    if (phoneInput) addPhoneToList();

    try {
      setIsLoading(true);
      const payload = {
        user_id: slug_uuid,
        go_notification_settings: {
          order_submission_reminder: formState?.order_submission_reminder,
          delivery_control_email_setting: {
            delivery_control_additional_email: formState?.delivery_control_additional_email_enabled,
            delivery_control_settings_emails: emailList.join(","),
            delivery_control_additional_sms: formState?.delivery_control_additional_sms,
            delivery_control_settings_phones: phoneList.join(","),
          },
        },
      };
      const response = await updateNotificationSettings(payload);
      dispatch({
        type: COMMON_CHANGE_SETTINGS,
        payload: {
          user_id: slug_uuid,
          data: {
            notification_settings: response?.notification_settings || {},
          },
        },
      });
      toast.success(response?.message);
      fetchNotificationSettings();
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <AnimatedLoader isLoading={isLoading} />
      <WidgetHeader
        user_id={slug_uuid}
        isJoin={isJoin}
        auth={settings?.auth}
        creator_id={slug_uuid}
      />
      <Helmet>
        <meta charSet="utf-8" />
        <title>Email Notifications</title>
      </Helmet>
      <ToastContainer progressClassName="fancy-progress-bar" />

      {modals?.isModalAuth && (
        <ModalAuth
          user_id={slug_uuid}
          error={form.form?.error?.message}
          show
          onHide={() =>
            dispatch({ type: CLOSE_MODAL_AUTH, payload: slug_uuid })
          }
        />
      )}
      {modals?.isModalConfirmation && (
        <ModalConfirmation
          user_id={slug_uuid}
          isResendCode={settings?.isResendCode}
          error={form.form?.error?.message}
          show
          order_deadline={groupOrders?.order_deadline * 1000}
          onHide={() =>
            dispatch({
              type: MODAL_TOGGLE,
              payload: { data: "isModalConfirmation", user_id: slug_uuid },
            })
          }
        />
      )}
      {modals?.isModalEditProfile && (
        <ModalEditProfile
          error={form.form?.error?.message}
          user_id={slug_uuid}
          groupOrders={groupOrders}
          show
          auth={settings?.auth}
          onHide={() =>
            dispatch({
              type: MODAL_TOGGLE,
              payload: { data: "isModalEditProfile", user_id: slug_uuid },
            })
          }
        />
      )}
      {modals?.isModalSetting && (
        <ModalSetting
          error={form.form?.error?.message}
          user_id={slug_uuid}
          groupOrders={groupOrders}
          show
          auth={settings?.auth}
          onHide={() =>
            dispatch({
              type: MODAL_TOGGLE,
              payload: { data: "isModalSetting", user_id: slug_uuid },
            })
          }
        />
      )}
      {modals?.isModalGroupSetting && (
        <ModalGroupSetting
          error={form.form?.error?.message}
          user_id={slug_uuid}
          groupOrders={groupOrders}
          show
          auth={settings?.auth}
          onHide={() =>
            dispatch({
              type: MODAL_TOGGLE,
              payload: { data: "isModalGroupSetting", user_id: slug_uuid },
            })
          }
        />
      )}

      <div style={{ height: "calc(100vh - 325px)", overflow: "auto" }} className="email-notification-container">
        <h2 className="email-notification-title">Email Notifications</h2>
        <div className="email-notification-card">
          <div className="email-notification-actions">
            <div className="email-notification-toggle">
              <label>
                <input
                  type="checkbox"
                  checked={formState.order_submission_reminder}
                  onChange={(e) =>
                    handleChange("order_submission_reminder", e.target.checked)
                  }
                />
                <span>Order submission reminder (email / sms)</span>
              </label>
            </div>

            <div>
              <div className="email-setting-row email-setting-row-with-input">
                <label className="email-setting-label">
                  <input
                    type="checkbox"
                    checked={formState.delivery_control_additional_email_enabled}
                    onChange={(e) =>
                      handleChange(
                        "delivery_control_additional_email_enabled",
                        e.target.checked
                      )
                    }
                  />
                  <span>Delivery Control Additional Emails Setting</span>
                </label>

                <div className="email-input-wrapper" style={{ display: "flex", alignItems: "center", width: "50%", justifyContent: "flex-end" }}>
                  <input
                    className="email-input-field"
                    type="text"
                    placeholder="Separate multiple email addresses by commas, e.g.: alison@example.com, martin@example.com"
                    value={emailInput}
                    disabled={!formState.delivery_control_additional_email_enabled}
                    onChange={handleEmailInputChange}
                  />
                  <button
                    type="button"
                    className="add-email-btn"
                    onClick={addEmailsToList}
                    disabled={!emailInput || !!emailError}
                  >
                    Add Email(s)
                  </button>
                </div>
              </div>
              {emailError && <div className="email-error-text">{emailError}</div>}
              {emailList.length > 0 && (
                <div className="email-chip-wrapper">
                  {emailList.map((email) => (
                    <span key={email} className="email-chip">
                      {email}
                      <button onClick={() => removeEmail(email)}>&times;</button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            <div className="email-setting-row email-setting-row-with-input">
              <label className="email-setting-label">
                <input
                  type="checkbox"
                  checked={formState.delivery_control_additional_sms}
                  onChange={(e) =>
                    handleChange("delivery_control_additional_sms", e.target.checked)
                  }
                />
                <span>Delivery Control Additional SMS Setting</span>
              </label>

              <div className="email-input-wrapper" style={{ display: "flex", alignItems: "center", width: "50%", justifyContent: "flex-end" }}>
                <input
                  className="email-input-field"
                  type="text"
                  placeholder="Separate multiple phone numbers by commas, e.g.: 6472345555, 6472335555"
                  value={phoneInput}
                  disabled={!formState.delivery_control_additional_sms}
                  onChange={handlePhoneInputChange}
                />
                <button
                  type="button"
                  className="add-email-btn"
                  onClick={addPhoneToList}
                  disabled={!phoneInput || !formState.delivery_control_additional_sms}
                >
                  Add Phone
                </button>
              </div>
            </div>

            {phoneList.length > 0 && (
              <div className="email-chip-wrapper">
                {phoneList.map((phone) => (
                  <span key={phone} className="email-chip">
                    {phone}
                    <button onClick={() => removePhone(phone)}>&times;</button>
                  </span>
                ))}
              </div>
            )}

            <div className="email-save-row">
              <button
                className="save-email-settings-btn"
                onClick={handleSave}
                disabled={isLoading}
              >
                Save Settings
              </button>
            </div>
          </div>
        </div>
      </div>
      <WidgetFooter />
    </>
  );
};

export default EmailNotification;
